import './ModalStartEvent.scss';

import { Alert, Divider, Grid } from '@mui/material';
import { EventSubStatusEnum } from 'domain/event/EventStatusEnum';
import { TypeEventCodeEnum } from 'domain/event/TypeEventCodeEnum';
import { PlayersNumberByTypeTeams } from 'domain/event/TypeTeam';
import { UnpyEvent } from 'domain/event/UnpyEvent';
import {
  getIsRandomTeamState,
  getMaxTeams,
  getMaxTeamsRandomState,
  getSubStatusOfEvent,
  maxMatchBySessionLigueRobin,
} from 'domain/event/UnpyEvent.func';
import { UnpyEventView } from 'domain/event/UnpyEventView';
import { GenerateMatchesForm } from 'primary/classements/Ligue/Modal/GenerateClassement/GenerateMatchesForm';
import { CoupleButtonValidCancel } from 'primary/Components/CoupleButtonValidCancel/CoupleButtonValidCancel';
import { KeyValue } from 'primary/Components/KeyValue/KeyValue';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useRetrieveFromDomain } from 'primary/hooks/useRetrieveFromDomain';
import React, { useState } from 'react';

import { Title } from '../../../../Components/Title/Title';
import { useTranslate } from '../../../../hooks/useTranslate';

interface ModalStartEventProps {
  handleClose: () => void;
  event: UnpyEvent | UnpyEventView;
  onStarted: () => void;
  regenerate?: boolean;
}

const ModalStartEvent = ({
  handleClose,
  event,
  onStarted,
  regenerate,
}: ModalStartEventProps) => {
  const t = useTranslate();
  const [loading, setLoading] = useState(false);
  const { eventRepository, matchsRepository, eventParticipantRepository } =
    useContextDependency();
  const [invalidParticipant, , loadingInvalidParticipants, retry] = useRetrieveFromDomain(
    () => eventParticipantRepository.getAllInvalidParticipant(event.id),
    undefined,
  );
  const generateBracket = () => {
    setLoading(true);
    if (!regenerate) {
      eventRepository
        .startEvent(event.id)
        .then(() => {
          onStarted();
          handleClose();
        })
        .finally(() => setLoading(false));
    } else {
      matchsRepository
        .generateMatchs({ eventId: event.id })
        .then(() => {
          onStarted();
          handleClose();
        })
        .finally(() => setLoading(false));
    }
  };

  const isTypeLigue =
    event.typeEvent.code === TypeEventCodeEnum.LIGUE ||
    event.typeEvent.code === TypeEventCodeEnum.LIG_BR;

  return (
    <div className={'modal-start-event'}>
      {!event?.randomTeams && (
        <Title
          threeQuarter
          bordered
          title={
            regenerate
              ? t('classement.parameters.regenerate')
              : getSubStatusOfEvent(event) === EventSubStatusEnum.SUBSCRIBE_OPEN
              ? t('event.modal.closeSubscribeAndStartEvent.title')
              : t('event.modal.startEvent.title')
          }
          level={2}
        />
      )}

      {event?.randomTeams && (
        <Title
          threeQuarter
          bordered
          title={
            regenerate
              ? t('event.modal.startEvent.titleRandomRegenerate')
              : t('event.modal.startEvent.titleRandom')
          }
          level={2}
        />
      )}

      <div className={'modal-start-event__content'}>
        {regenerate && (
          <Alert severity="warning">{t('classement.parameters.regenerateNotice')}</Alert>
        )}
        {!regenerate && (
          <Alert severity="warning" className={'-alertDelayed'}>
            {t('classement.parameters.beginNotice')}
          </Alert>
        )}
        <div className={'-containerKeyValue'}>
          <h3>{t('event.modal.generateClassement.form.title')}</h3>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <KeyValue
                alignStart
                keyValue={t('event.modal.generateClassement.typeEvent.label')}
                value={t(`enum.typeEventCode.${event.typeEvent.code}`)}
                className={'-keyValue'}
                accent
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <KeyValue
                alignStart
                keyValue={t('event.modal.generateClassement.game.label')}
                value={t(`games.libelle.${event.game.code}`)}
                className={'-keyValue'}
                accent
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <KeyValue
                absolutePopper
                alignStart
                keyValue={t('event.eventViewPage.randomTeamsLabel')}
                value={event?.randomTeams ? 'Oui' : 'Non'}
                className={'-keyValue'}
                accent
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <KeyValue
                absolutePopper
                alignStart
                keyValue={t('event.eventViewPage.infos.maxParticipantValid')}
                value={`${
                  (event?.totalParticipants ?? 0) - (invalidParticipant?.length ?? 0)
                } / ${
                  getIsRandomTeamState(event)
                    ? getMaxTeamsRandomState(event)
                    : getMaxTeams(event)
                }`}
                className={'-keyValue'}
                accent
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {event.typeEvent.code === TypeEventCodeEnum.LIGUE && (
                <KeyValue
                  alignStart
                  keyValue={t('event.modal.generateClassement.ronde.label')}
                  value={t('event.modal.generateClassement.ronde.robinLabel')}
                  className={'-keyValue'}
                  accent
                />
              )}
              {event.typeEvent.code === TypeEventCodeEnum.TOURNAMENT && (
                <KeyValue
                  alignStart
                  keyValue={t('event.modal.generateClassement.typeTournois.label')}
                  value={t('event.modal.generateClassement.typeTournois.simpleElem')}
                  className={'-keyValue'}
                  accent
                />
              )}
              {event.typeEvent.code === TypeEventCodeEnum.TOURNAMENT_DOUBLE_ELEM && (
                <KeyValue
                  alignStart
                  keyValue={t('event.modal.generateClassement.typeTournois.label')}
                  value={t('event.modal.generateClassement.typeTournois.doubleElem')}
                  className={'-keyValue'}
                  accent
                />
              )}
            </Grid>

            {event?.randomTeams && (
              <Grid item xs={12} sm={6}>
                <KeyValue
                  alignStart
                  keyValue={t('event.eventViewPage.infos.maxSquadAfterGenerateSquad')}
                  value={`${getMaxTeams(event)} équipes de ${
                    //@ts-ignore
                    PlayersNumberByTypeTeams[event?.typeTeam?.code]?.length
                  } J`}
                  className={'-keyValue'}
                  accent
                />
              </Grid>
            )}
            {(invalidParticipant?.length ?? 0) > 0 && (
              <Grid item xs={12} sm={6}>
                <KeyValue
                  absolutePopper
                  alignStart
                  keyValue={'Équipes invalides :'}
                  value={invalidParticipant?.map((p) => p.team.name).join(', ')}
                  className={'-keyValue'}
                  accent
                />
              </Grid>
            )}
          </Grid>
          <div className={'-containerKeyValue__info'}>
            {event.randomTeams && (
              <Alert severity={'info'}>
                <span>{t('event.modal.startEvent.titleRandomNotice')}</span>
              </Alert>
            )}
          </div>
        </div>
        <Divider />
        {!loadingInvalidParticipants && isTypeLigue && (
          <div className={'-form'}>
            <div className={'generateMatchFormContainer'}>
              <GenerateMatchesForm
                event={event}
                totalMatch={maxMatchBySessionLigueRobin(event, invalidParticipant)}
                handleClose={handleClose}
                onSubmitted={onStarted}
                isForStart={!regenerate}
                invalidParticipants={invalidParticipant}
              />
            </div>
          </div>
        )}
        {!loadingInvalidParticipants &&
          (event.typeEvent.code === TypeEventCodeEnum.TOURNAMENT ||
            event.typeEvent.code === TypeEventCodeEnum.TOURNAMENT_DOUBLE_ELEM) && (
            <CoupleButtonValidCancel
              onClickCancel={handleClose}
              loading={loading}
              onClickValid={generateBracket}
            />
          )}
      </div>
    </div>
  );
};

export default ModalStartEvent;
